// src/components/AgentDevProfilePanel.jsx
//
// Shows the user what their agent knows about them (devProfile),
// lets them control proactive outreach cadence and response timing,
// and lets them delete their profile for a specific agent.
//
// Rendered as a Dialog triggered by a settings icon in AgentFocusModal.
// Self-contained — no Redux dependency, calls m172 directly with the
// user's Firebase ID token.

import React, { Component } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Switch,
  Select,
  MenuItem,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Tooltip,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CloseIcon         from '@material-ui/icons/Close';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import InfoOutlinedIcon  from '@material-ui/icons/InfoOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
// CHANGED: m172 client moved to a shared util (also used by the report dialog)
import { callM172 as callM172Shared, describeError } from '../utils/m172Client';
import AgentDevReportDialog from './AgentDevReportDialog';   // NEW

const styles = theme => ({
  dialogTitle: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    paddingBottom:  0,
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionLabel: {
    fontSize:      12,
    fontWeight:    600,
    color:         theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom:  theme.spacing(1),
  },
  synthesis: {
    fontSize:   15,
    lineHeight: 1.6,
    color:      theme.palette.text.primary,
  },
  chipRow: {
    display:    'flex',
    flexWrap:   'wrap',
    gap:        theme.spacing(0.75),
    marginTop:  theme.spacing(0.5),
    alignItems: 'flex-start',
  },
  chip: {
    fontSize:   13,
    height:     'auto',
    whiteSpace: 'normal',
    '& .MuiChip-label': {
      whiteSpace:    'normal',
      paddingTop:    6,
      paddingBottom: 6,
    },
  },
  commitmentItem: {
    display:      'flex',
    alignItems:   'flex-start',
    marginBottom: theme.spacing(1),
  },
  commitmentDot: {
    width:        6,
    height:       6,
    borderRadius: '50%',
    background:   theme.palette.primary.main,
    marginTop:    7,
    marginRight:  theme.spacing(1.25),
    flexShrink:   0,
  },
  commitmentText: {
    fontSize:   14,
    lineHeight: 1.5,
  },
  commitmentDate: {
    fontSize:  12,
    color:     theme.palette.text.secondary,
    marginTop: 2,
  },
  powerRow: {
    display:        'flex',
    justifyContent: 'space-between',
    alignItems:     'center',
    padding:        `${theme.spacing(0.5)}px 0`,
  },
  powerName: {
    fontSize:      14,
    textTransform: 'capitalize',
  },
  powerCount: {
    fontSize: 13,
    color:    theme.palette.text.secondary,
  },
  nextFocus: {
    fontSize:    14,
    lineHeight:  1.6,
    fontStyle:   'italic',
    color:       theme.palette.text.secondary,
    borderLeft:  `3px solid ${theme.palette.primary.main}`,
    paddingLeft: theme.spacing(1.5),
    marginTop:   theme.spacing(0.5),
  },
  outreachRow: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    marginBottom:   theme.spacing(1.5),
  },
  deleteSection: {
    marginTop:  theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop:  `1px solid ${theme.palette.divider}`,
  },
  deleteButton: {
    color: theme.palette.error.main,
  },
  reportBox: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    gap:            12,
    background:     '#eef4fb',
    borderRadius:   8,
    padding:        theme.spacing(1.5, 2),
    marginBottom:   theme.spacing(3),
    flexWrap:       'wrap',
  },
  emptyState: {
    textAlign: 'center',
    padding:   theme.spacing(4, 2),
    color:     theme.palette.text.secondary,
  },
});

class AgentDevProfilePanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading:         true,
      saving:          false,
      deleting:        false,
      devProfile:      null,
      outreachEnabled: true,
      maxPerWeek:      2,
      delayEnabled:    true,
      delayMaxSeconds: 10,
      error:           null,
      saveError:       null,   // CHANGED: save errors no longer hide the whole panel
      confirmDelete:   false,
      deleteSuccess:   false,
      saveSuccess:     false,
      reportOpen:      false,   // NEW
    };
  }

  componentDidMount() {
    if (this.props.open) this.loadProfile();
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      this.loadProfile();
    }
  }

  // CHANGED: delegates to the shared m172 client (adds the ID token).
  callM172(operation, extra = {}) {
    const { uid, agentId } = this.props;
    return callM172Shared({ uid, agentId, operation, extra });
  }

  async loadProfile() {
    this.setState({
      loading: true, error: null, saveError: null,
      deleteSuccess: false, saveSuccess: false,
    });

    try {
      const res  = await this.callM172('get');
      const data = res.data || {};

      // CHANGED: only a genuine "no record" shows the empty state.
      // Any other failure (400/401/500) is shown as an error.
      if (res.status === 404 || (data.success && !data.devProfile)) {
        this.setState({ loading: false, devProfile: null });
        return;
      }
      if (!data.success) {
        this.setState({
          loading: false,
          error: data.error || `Could not load your profile (HTTP ${res.status}).`,
        });
        return;
      }

      const profile = data.devProfile;
      const prefs   = profile.outreachPrefs || {};
      const delay   = profile.responseDelay || {};

      this.setState({
        loading:         false,
        devProfile:      profile,
        outreachEnabled: prefs.enabled !== false,
        maxPerWeek:      prefs.maxPerWeek || 2,
        delayEnabled:    delay.enabled !== false,
        delayMaxSeconds: typeof delay.maxSeconds === 'number' ? delay.maxSeconds : 10,
      });

    } catch (e) {
      this.setState({ loading: false, error: describeError(e) });
    }
  }

  async savePrefs() {
    const { outreachEnabled, maxPerWeek, delayEnabled, delayMaxSeconds } = this.state;
    this.setState({ saving: true, saveError: null });

    try {
      const res = await this.callM172('updatePrefs', {
        prefs: {
          outreachEnabled,
          outreachMaxPerWeek: maxPerWeek,
          delayEnabled,
          delayMaxSeconds,
        },
      });
      const data = res.data || {};

      if (res.status === 404) {
        this.setState({
          saving: false,
          saveError: 'Start a conversation with this agent first, then save your preferences.',
        });
        return;
      }
      if (!data.success) {
        this.setState({
          saving: false,
          saveError: data.error || `Could not save (HTTP ${res.status}).`,
        });
        return;
      }

      this.setState({ saving: false, saveSuccess: true });
      setTimeout(() => this.setState({ saveSuccess: false }), 2000);

    } catch (e) {
      this.setState({ saving: false, saveError: describeError(e) });
    }
  }

  async deleteProfile() {
    this.setState({ deleting: true, confirmDelete: false });

    try {
      const res  = await this.callM172('delete');
      const data = res.data || {};
      if (!data.success) {
        throw new Error(data.error || `Could not delete (HTTP ${res.status}).`);
      }
      this.setState({ deleting: false, devProfile: null, deleteSuccess: true });
    } catch (e) {
      this.setState({ deleting: false, error: describeError(e) });
    }
  }

  renderProfile() {
    const { classes }    = this.props;
    const { devProfile } = this.state;

    if (!devProfile || !devProfile.synthesis) {
      return (
        <div className={classes.emptyState}>
          <InfoOutlinedIcon style={{ fontSize: 40, marginBottom: 8, opacity: 0.4 }} />
          <Typography variant="body2">
            No development profile yet. Continue your conversations and one will build naturally.
          </Typography>
        </div>
      );
    }

    const openCommitments = (devProfile.openCommitments || []).filter(c => !c.followedUp);

    const powers = Object.entries(devProfile.powersWorked || {})
      .filter(([, count]) => count > 0)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3);

    return (
      <>
        {/* NEW: development report entry point */}
        <div className={classes.reportBox}>
          <Typography variant="body2" style={{ flex: '1 1 220px' }}>
            Ask your agent for a written report on your development, progress and commitments.
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<DescriptionOutlinedIcon />}
            onClick={() => this.setState({ reportOpen: true })}
            style={{ background: '#1a6fba', color: '#fff', textTransform: 'none' }}
          >
            View my development report
          </Button>
        </div>

        <div className={classes.section}>
          <Typography className={classes.sectionLabel}>Where you are right now</Typography>
          <Typography className={classes.synthesis}>{devProfile.synthesis}</Typography>
        </div>

        {devProfile.growthEdges?.length > 0 && (
          <div className={classes.section}>
            <Typography className={classes.sectionLabel}>Current growth edges</Typography>
            <div className={classes.chipRow}>
              {devProfile.growthEdges.map((edge, i) => (
                <Chip
                  key={i}
                  label={edge}
                  size="small"
                  className={classes.chip}
                  style={{ height: 'auto', whiteSpace: 'normal', maxWidth: '100%' }}
                />
              ))}
            </div>
          </div>
        )}

        {openCommitments.length > 0 && (
          <div className={classes.section}>
            <Typography className={classes.sectionLabel}>Open commitments</Typography>
            {openCommitments.map((c, i) => (
              <div key={i} className={classes.commitmentItem}>
                <div className={classes.commitmentDot} />
                <div>
                  <Typography className={classes.commitmentText}>{c.text}</Typography>
                  {c.madeOn && (
                    <Typography className={classes.commitmentDate}>Committed {c.madeOn}</Typography>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {devProfile.nextSuggestedFocus && (
          <div className={classes.section}>
            <Typography className={classes.sectionLabel}>What your agent is holding for next time</Typography>
            <Typography className={classes.nextFocus}>{devProfile.nextSuggestedFocus}</Typography>
          </div>
        )}

        {powers.length > 0 && (
          <div className={classes.section}>
            <Typography className={classes.sectionLabel}>Most developed Human Powers</Typography>
            {powers.map(([power, count]) => (
              <div key={power} className={classes.powerRow}>
                <Typography className={classes.powerName}>{power}</Typography>
                <Typography className={classes.powerCount}>
                  {count} session{count !== 1 ? 's' : ''}
                </Typography>
              </div>
            ))}
          </div>
        )}
      </>
    );
  }

  renderOutreachControls() {
    const { classes } = this.props;
    const { outreachEnabled, maxPerWeek, saving, saveSuccess, saveError } = this.state;

    return (
      <div className={classes.section}>
        <Divider style={{ marginBottom: 20 }} />
        <Typography className={classes.sectionLabel}>Proactive outreach</Typography>

        <div className={classes.outreachRow}>
          <Typography variant="body2">
            Allow your agent to reach out between sessions
          </Typography>
          <Switch
            checked={outreachEnabled}
            onChange={e => this.setState({ outreachEnabled: e.target.checked })}
            color="primary"
            size="small"
          />
        </div>

        {outreachEnabled && (
          <div className={classes.outreachRow}>
            <Typography variant="body2">Maximum messages per week</Typography>
            <Select
              value={maxPerWeek}
              onChange={e => this.setState({ maxPerWeek: e.target.value })}
              style={{ minWidth: 60 }}
              variant="outlined"
              MenuProps={{ style: { zIndex: 1600 } }}
            >
              {[1, 2, 3, 4, 5].map(n => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </Select>
          </div>
        )}

        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={() => this.savePrefs()}
          disabled={saving}
          style={{ marginTop: 4 }}
        >
          {saving ? <CircularProgress size={16} style={{ marginRight: 6 }} /> : null}
          {saveSuccess ? 'Saved ✓' : 'Save settings'}
        </Button>

        {/* CHANGED: inline save error, panel stays visible */}
        {saveError && (
          <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
            {saveError}
          </Typography>
        )}
      </div>
    );
  }

  renderDelayControls() {
    const { classes } = this.props;
    const { delayEnabled, delayMaxSeconds } = this.state;

    return (
      <div className={classes.section}>
        <Typography className={classes.sectionLabel}>Response timing</Typography>
        <Typography variant="caption" display="block" color="textSecondary" style={{ marginBottom: 8 }}>
          Your agent pauses before starting to reply, so it feels less mechanical. Pick the maximum length of that pause — the actual pause is a random amount up to this value.
        </Typography>

        <div className={classes.outreachRow}>
          <Typography variant="body2">Add a natural response pause</Typography>
          <Switch
            checked={delayEnabled}
            onChange={e => this.setState({ delayEnabled: e.target.checked })}
            color="primary"
            size="small"
          />
        </div>

        {delayEnabled && (
          <div className={classes.outreachRow}>
            <Typography variant="body2">Maximum pause</Typography>
            <Select
              value={delayMaxSeconds}
              onChange={e => this.setState({ delayMaxSeconds: e.target.value })}
              style={{ minWidth: 80 }}
              variant="outlined"
              MenuProps={{ style: { zIndex: 1600 } }}
            >
              {[3, 5, 10, 15].map(n => (
                <MenuItem key={n} value={n}>{n}s</MenuItem>
              ))}
            </Select>
          </div>
        )}
      </div>
    );
  }

  render() {
    const { open, onClose, classes, agentLabel, uid, agentId } = this.props;
    const { loading, error, devProfile, deleting, confirmDelete, deleteSuccess, reportOpen } = this.state;

    return (
      <>
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper" style={{ zIndex: 1500 }}>
          <DialogTitle disableTypography className={classes.dialogTitle}>
            <Typography variant="h6" style={{ fontSize: 16 }}>
              {agentLabel} — Your Development Profile
            </Typography>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            {loading && (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <CircularProgress size={28} />
              </div>
            )}

            {!loading && error && (
              <Typography color="error" variant="body2">{error}</Typography>
            )}

            {!loading && deleteSuccess && (
              <div className={classes.emptyState}>
                <Typography variant="body2">
                  Your development profile has been deleted. It will rebuild naturally as you continue your conversations.
                </Typography>
              </div>
            )}

            {!loading && !error && !deleteSuccess && (
              <>
                {this.renderProfile()}
                {this.renderDelayControls()}
                {this.renderOutreachControls()}

                <div className={classes.deleteSection}>
                  <Tooltip title="Remove what your agent currently knows about your development. Your conversation history and settings are not affected.">
                    <Button
                      className={classes.deleteButton}
                      startIcon={<DeleteOutlineIcon />}
                      size="small"
                      onClick={() => this.setState({ confirmDelete: true })}
                      disabled={deleting || !devProfile}
                    >
                      {deleting ? 'Deleting…' : 'Delete development profile'}
                    </Button>
                  </Tooltip>
                  <Typography variant="caption" display="block" color="textSecondary" style={{ marginTop: 4 }}>
                    Removes what the agent knows about your growth edges, commitments, and patterns, and the report built from them. Your conversation history and settings are not affected.
                  </Typography>
                </div>
              </>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={onClose} size="small">Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={confirmDelete}
          onClose={() => this.setState({ confirmDelete: false })}
          maxWidth="xs"
          style={{ zIndex: 1500 }}
        >
          <DialogTitle>Delete development profile?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              This removes what your agent currently knows about your growth edges, open commitments, and developmental patterns. Your conversation history and settings are not affected. The profile will rebuild naturally as you continue talking.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ confirmDelete: false })} size="small">
              Cancel
            </Button>
            <Button onClick={() => this.deleteProfile()} color="secondary" size="small">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* NEW: separate popup for the agent-written report */}
        <AgentDevReportDialog
          open={reportOpen}
          onClose={() => this.setState({ reportOpen: false })}
          uid={uid}
          agentId={agentId}
          agentLabel={agentLabel}
        />
      </>
    );
  }
}

export default withStyles(styles)(AgentDevProfilePanel);
/////////////////////////////////////////////////////////////////////////////////////
// // src/components/AgentDevProfilePanel.jsx
// //
// // Shows the user what their agent knows about them (devProfile),
// // lets them control proactive outreach cadence and response timing,
// // and lets them delete their profile for a specific agent.
// //
// // Rendered as a Dialog triggered by a settings icon in AgentFocusModal.
// // Self-contained — no Redux dependency, calls m172 directly with the
// // user's Firebase ID token.

// import React, { Component } from 'react';
// import {
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   IconButton,
//   Typography,
//   Switch,
//   Select,
//   MenuItem,
//   Button,
//   Chip,
//   Divider,
//   CircularProgress,
//   Tooltip,
// } from '@material-ui/core';
// import { withStyles } from '@material-ui/core/styles';
// import CloseIcon         from '@material-ui/icons/Close';
// import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
// import InfoOutlinedIcon  from '@material-ui/icons/InfoOutlined';
// import axios from 'axios';
// import { FB_URL } from '../store/actions/types';

// // CHANGED: needed for the ID token. ADJUST this import to match how
// // App.js / fbConfig.js import Firebase — it must be the SAME instance
// // the app initialised, or currentUser will be null.
// import firebase from 'firebase/compat/app';
// import 'firebase/compat/auth';

// const M172_URL = `${FB_URL}/m172-agentDevProfileManager`;

// // CHANGED: works whether firebase.auth is the namespace function
// // (firebase.auth()) or an already-created instance (firebase.auth).
// async function getAuthHeaders() {
//   const auth = typeof firebase.auth === 'function' ? firebase.auth() : firebase.auth;
//   const user = auth && auth.currentUser;
//   if (!user) throw new Error('You are signed out. Please sign in again.');
//   const token = await user.getIdToken();
//   return { Authorization: `Bearer ${token}` };
// }

// // CHANGED: pulls the server's actual error message out of an axios error,
// // instead of the generic "Request failed with status code 400".
// function describeError(e) {
//   const status    = e.response?.status;
//   const serverMsg = e.response?.data?.error || e.response?.data?.message;
//   if (e.response) console.error('m172 request failed:', status, e.response.data);
//   return serverMsg || e.message;
// }

// const styles = theme => ({
//   dialogTitle: {
//     display:        'flex',
//     alignItems:     'center',
//     justifyContent: 'space-between',
//     paddingBottom:  0,
//   },
//   section: {
//     marginBottom: theme.spacing(3),
//   },
//   sectionLabel: {
//     fontSize:      12,
//     fontWeight:    600,
//     color:         theme.palette.text.secondary,
//     textTransform: 'uppercase',
//     letterSpacing: 1,
//     marginBottom:  theme.spacing(1),
//   },
//   synthesis: {
//     fontSize:   15,
//     lineHeight: 1.6,
//     color:      theme.palette.text.primary,
//   },
//   chipRow: {
//     display:    'flex',
//     flexWrap:   'wrap',
//     gap:        theme.spacing(0.75),
//     marginTop:  theme.spacing(0.5),
//     alignItems: 'flex-start',
//   },
//   chip: {
//     fontSize:   13,
//     height:     'auto',
//     whiteSpace: 'normal',
//     '& .MuiChip-label': {
//       whiteSpace:    'normal',
//       paddingTop:    6,
//       paddingBottom: 6,
//     },
//   },
//   commitmentItem: {
//     display:      'flex',
//     alignItems:   'flex-start',
//     marginBottom: theme.spacing(1),
//   },
//   commitmentDot: {
//     width:        6,
//     height:       6,
//     borderRadius: '50%',
//     background:   theme.palette.primary.main,
//     marginTop:    7,
//     marginRight:  theme.spacing(1.25),
//     flexShrink:   0,
//   },
//   commitmentText: {
//     fontSize:   14,
//     lineHeight: 1.5,
//   },
//   commitmentDate: {
//     fontSize:  12,
//     color:     theme.palette.text.secondary,
//     marginTop: 2,
//   },
//   powerRow: {
//     display:        'flex',
//     justifyContent: 'space-between',
//     alignItems:     'center',
//     padding:        `${theme.spacing(0.5)}px 0`,
//   },
//   powerName: {
//     fontSize:      14,
//     textTransform: 'capitalize',
//   },
//   powerCount: {
//     fontSize: 13,
//     color:    theme.palette.text.secondary,
//   },
//   nextFocus: {
//     fontSize:    14,
//     lineHeight:  1.6,
//     fontStyle:   'italic',
//     color:       theme.palette.text.secondary,
//     borderLeft:  `3px solid ${theme.palette.primary.main}`,
//     paddingLeft: theme.spacing(1.5),
//     marginTop:   theme.spacing(0.5),
//   },
//   outreachRow: {
//     display:        'flex',
//     alignItems:     'center',
//     justifyContent: 'space-between',
//     marginBottom:   theme.spacing(1.5),
//   },
//   deleteSection: {
//     marginTop:  theme.spacing(2),
//     paddingTop: theme.spacing(2),
//     borderTop:  `1px solid ${theme.palette.divider}`,
//   },
//   deleteButton: {
//     color: theme.palette.error.main,
//   },
//   emptyState: {
//     textAlign: 'center',
//     padding:   theme.spacing(4, 2),
//     color:     theme.palette.text.secondary,
//   },
// });

// class AgentDevProfilePanel extends Component {
//   constructor(props) {
//     super(props);
//     this.state = {
//       loading:         true,
//       saving:          false,
//       deleting:        false,
//       devProfile:      null,
//       outreachEnabled: true,
//       maxPerWeek:      2,
//       delayEnabled:    true,
//       delayMaxSeconds: 10,
//       error:           null,
//       saveError:       null,   // CHANGED: save errors no longer hide the whole panel
//       confirmDelete:   false,
//       deleteSuccess:   false,
//       saveSuccess:     false,
//     };
//   }

//   componentDidMount() {
//     if (this.props.open) this.loadProfile();
//   }

//   componentDidUpdate(prevProps) {
//     if (this.props.open && !prevProps.open) {
//       this.loadProfile();
//     }
//   }

//   // CHANGED: every call goes through here — adds the ID token.
//   // uid is still sent in the body so this frontend works against the
//   // OLD m172 during rollout; the new m172 ignores it.
//   async callM172(operation, extra = {}) {
//     const { uid, agentId } = this.props;
//     const headers = await getAuthHeaders();
//     return axios.post(
//       M172_URL,
//       { params: { agentId, uid, operation, ...extra } },
//       { headers, validateStatus: () => true }
//     );
//   }

//   async loadProfile() {
//     this.setState({
//       loading: true, error: null, saveError: null,
//       deleteSuccess: false, saveSuccess: false,
//     });

//     try {
//       const res  = await this.callM172('get');
//       const data = res.data || {};

//       // CHANGED: only a genuine "no record" shows the empty state.
//       // Any other failure (400/401/500) is shown as an error.
//       if (res.status === 404 || (data.success && !data.devProfile)) {
//         this.setState({ loading: false, devProfile: null });
//         return;
//       }
//       if (!data.success) {
//         this.setState({
//           loading: false,
//           error: data.error || `Could not load your profile (HTTP ${res.status}).`,
//         });
//         return;
//       }

//       const profile = data.devProfile;
//       const prefs   = profile.outreachPrefs || {};
//       const delay   = profile.responseDelay || {};

//       this.setState({
//         loading:         false,
//         devProfile:      profile,
//         outreachEnabled: prefs.enabled !== false,
//         maxPerWeek:      prefs.maxPerWeek || 2,
//         delayEnabled:    delay.enabled !== false,
//         delayMaxSeconds: typeof delay.maxSeconds === 'number' ? delay.maxSeconds : 10,
//       });

//     } catch (e) {
//       this.setState({ loading: false, error: describeError(e) });
//     }
//   }

//   async savePrefs() {
//     const { outreachEnabled, maxPerWeek, delayEnabled, delayMaxSeconds } = this.state;
//     this.setState({ saving: true, saveError: null });

//     try {
//       const res = await this.callM172('updatePrefs', {
//         prefs: {
//           outreachEnabled,
//           outreachMaxPerWeek: maxPerWeek,
//           delayEnabled,
//           delayMaxSeconds,
//         },
//       });
//       const data = res.data || {};

//       if (res.status === 404) {
//         this.setState({
//           saving: false,
//           saveError: 'Start a conversation with this agent first, then save your preferences.',
//         });
//         return;
//       }
//       if (!data.success) {
//         this.setState({
//           saving: false,
//           saveError: data.error || `Could not save (HTTP ${res.status}).`,
//         });
//         return;
//       }

//       this.setState({ saving: false, saveSuccess: true });
//       setTimeout(() => this.setState({ saveSuccess: false }), 2000);

//     } catch (e) {
//       this.setState({ saving: false, saveError: describeError(e) });
//     }
//   }

//   async deleteProfile() {
//     this.setState({ deleting: true, confirmDelete: false });

//     try {
//       const res  = await this.callM172('delete');
//       const data = res.data || {};
//       if (!data.success) {
//         throw new Error(data.error || `Could not delete (HTTP ${res.status}).`);
//       }
//       this.setState({ deleting: false, devProfile: null, deleteSuccess: true });
//     } catch (e) {
//       this.setState({ deleting: false, error: describeError(e) });
//     }
//   }

//   renderProfile() {
//     const { classes }    = this.props;
//     const { devProfile } = this.state;

//     if (!devProfile || !devProfile.synthesis) {
//       return (
//         <div className={classes.emptyState}>
//           <InfoOutlinedIcon style={{ fontSize: 40, marginBottom: 8, opacity: 0.4 }} />
//           <Typography variant="body2">
//             No development profile yet. Continue your conversations and one will build naturally.
//           </Typography>
//         </div>
//       );
//     }

//     const openCommitments = (devProfile.openCommitments || []).filter(c => !c.followedUp);

//     const powers = Object.entries(devProfile.powersWorked || {})
//       .filter(([, count]) => count > 0)
//       .sort(([, a], [, b]) => b - a)
//       .slice(0, 3);

//     return (
//       <>
//         <div className={classes.section}>
//           <Typography className={classes.sectionLabel}>Where you are right now</Typography>
//           <Typography className={classes.synthesis}>{devProfile.synthesis}</Typography>
//         </div>

//         {devProfile.growthEdges?.length > 0 && (
//           <div className={classes.section}>
//             <Typography className={classes.sectionLabel}>Current growth edges</Typography>
//             <div className={classes.chipRow}>
//               {devProfile.growthEdges.map((edge, i) => (
//                 <Chip
//                   key={i}
//                   label={edge}
//                   size="small"
//                   className={classes.chip}
//                   style={{ height: 'auto', whiteSpace: 'normal', maxWidth: '100%' }}
//                 />
//               ))}
//             </div>
//           </div>
//         )}

//         {openCommitments.length > 0 && (
//           <div className={classes.section}>
//             <Typography className={classes.sectionLabel}>Open commitments</Typography>
//             {openCommitments.map((c, i) => (
//               <div key={i} className={classes.commitmentItem}>
//                 <div className={classes.commitmentDot} />
//                 <div>
//                   <Typography className={classes.commitmentText}>{c.text}</Typography>
//                   {c.madeOn && (
//                     <Typography className={classes.commitmentDate}>Committed {c.madeOn}</Typography>
//                   )}
//                 </div>
//               </div>
//             ))}
//           </div>
//         )}

//         {devProfile.nextSuggestedFocus && (
//           <div className={classes.section}>
//             <Typography className={classes.sectionLabel}>What your agent is holding for next time</Typography>
//             <Typography className={classes.nextFocus}>{devProfile.nextSuggestedFocus}</Typography>
//           </div>
//         )}

//         {powers.length > 0 && (
//           <div className={classes.section}>
//             <Typography className={classes.sectionLabel}>Most developed Human Powers</Typography>
//             {powers.map(([power, count]) => (
//               <div key={power} className={classes.powerRow}>
//                 <Typography className={classes.powerName}>{power}</Typography>
//                 <Typography className={classes.powerCount}>
//                   {count} session{count !== 1 ? 's' : ''}
//                 </Typography>
//               </div>
//             ))}
//           </div>
//         )}
//       </>
//     );
//   }

//   renderOutreachControls() {
//     const { classes } = this.props;
//     const { outreachEnabled, maxPerWeek, saving, saveSuccess, saveError } = this.state;

//     return (
//       <div className={classes.section}>
//         <Divider style={{ marginBottom: 20 }} />
//         <Typography className={classes.sectionLabel}>Proactive outreach</Typography>

//         <div className={classes.outreachRow}>
//           <Typography variant="body2">
//             Allow your agent to reach out between sessions
//           </Typography>
//           <Switch
//             checked={outreachEnabled}
//             onChange={e => this.setState({ outreachEnabled: e.target.checked })}
//             color="primary"
//             size="small"
//           />
//         </div>

//         {outreachEnabled && (
//           <div className={classes.outreachRow}>
//             <Typography variant="body2">Maximum messages per week</Typography>
//             <Select
//               value={maxPerWeek}
//               onChange={e => this.setState({ maxPerWeek: e.target.value })}
//               style={{ minWidth: 60 }}
//               variant="outlined"
//               MenuProps={{ style: { zIndex: 1600 } }}
//             >
//               {[1, 2, 3, 4, 5].map(n => (
//                 <MenuItem key={n} value={n}>{n}</MenuItem>
//               ))}
//             </Select>
//           </div>
//         )}

//         <Button
//           variant="outlined"
//           color="primary"
//           size="small"
//           onClick={() => this.savePrefs()}
//           disabled={saving}
//           style={{ marginTop: 4 }}
//         >
//           {saving ? <CircularProgress size={16} style={{ marginRight: 6 }} /> : null}
//           {saveSuccess ? 'Saved ✓' : 'Save settings'}
//         </Button>

//         {/* CHANGED: inline save error, panel stays visible */}
//         {saveError && (
//           <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
//             {saveError}
//           </Typography>
//         )}
//       </div>
//     );
//   }

//   renderDelayControls() {
//     const { classes } = this.props;
//     const { delayEnabled, delayMaxSeconds } = this.state;

//     return (
//       <div className={classes.section}>
//         <Typography className={classes.sectionLabel}>Response timing</Typography>
//         <Typography variant="caption" display="block" color="textSecondary" style={{ marginBottom: 8 }}>
//           Your agent pauses before starting to reply, so it feels less mechanical. Pick the maximum length of that pause — the actual pause is a random amount up to this value.
//         </Typography>

//         <div className={classes.outreachRow}>
//           <Typography variant="body2">Add a natural response pause</Typography>
//           <Switch
//             checked={delayEnabled}
//             onChange={e => this.setState({ delayEnabled: e.target.checked })}
//             color="primary"
//             size="small"
//           />
//         </div>

//         {delayEnabled && (
//           <div className={classes.outreachRow}>
//             <Typography variant="body2">Maximum pause</Typography>
//             <Select
//               value={delayMaxSeconds}
//               onChange={e => this.setState({ delayMaxSeconds: e.target.value })}
//               style={{ minWidth: 80 }}
//               variant="outlined"
//               MenuProps={{ style: { zIndex: 1600 } }}
//             >
//               {[3, 5, 10, 15].map(n => (
//                 <MenuItem key={n} value={n}>{n}s</MenuItem>
//               ))}
//             </Select>
//           </div>
//         )}
//       </div>
//     );
//   }

//   render() {
//     const { open, onClose, classes, agentLabel } = this.props;
//     const { loading, error, devProfile, deleting, confirmDelete, deleteSuccess } = this.state;

//     return (
//       <>
//         <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper" style={{ zIndex: 1500 }}>
//           <DialogTitle disableTypography className={classes.dialogTitle}>
//             <Typography variant="h6" style={{ fontSize: 16 }}>
//               {agentLabel} — Your Development Profile
//             </Typography>
//             <IconButton size="small" onClick={onClose}>
//               <CloseIcon fontSize="small" />
//             </IconButton>
//           </DialogTitle>

//           <DialogContent dividers>
//             {loading && (
//               <div style={{ textAlign: 'center', padding: '32px 0' }}>
//                 <CircularProgress size={28} />
//               </div>
//             )}

//             {!loading && error && (
//               <Typography color="error" variant="body2">{error}</Typography>
//             )}

//             {!loading && deleteSuccess && (
//               <div className={classes.emptyState}>
//                 <Typography variant="body2">
//                   Your development profile has been deleted. It will rebuild naturally as you continue your conversations.
//                 </Typography>
//               </div>
//             )}

//             {!loading && !error && !deleteSuccess && (
//               <>
//                 {this.renderProfile()}
//                 {this.renderDelayControls()}
//                 {this.renderOutreachControls()}

//                 <div className={classes.deleteSection}>
//                   <Tooltip title="Remove what your agent currently knows about your development. Your conversation history and settings are not affected.">
//                     <Button
//                       className={classes.deleteButton}
//                       startIcon={<DeleteOutlineIcon />}
//                       size="small"
//                       onClick={() => this.setState({ confirmDelete: true })}
//                       disabled={deleting || !devProfile}
//                     >
//                       {deleting ? 'Deleting…' : 'Delete development profile'}
//                     </Button>
//                   </Tooltip>
//                   <Typography variant="caption" display="block" color="textSecondary" style={{ marginTop: 4 }}>
//                     Removes what the agent knows about your growth edges, commitments, and patterns. Your conversation history and settings are not affected.
//                   </Typography>
//                 </div>
//               </>
//             )}
//           </DialogContent>

//           <DialogActions>
//             <Button onClick={onClose} size="small">Close</Button>
//           </DialogActions>
//         </Dialog>

//         <Dialog
//           open={confirmDelete}
//           onClose={() => this.setState({ confirmDelete: false })}
//           maxWidth="xs"
//           style={{ zIndex: 1500 }}
//         >
//           <DialogTitle>Delete development profile?</DialogTitle>
//           <DialogContent>
//             <Typography variant="body2">
//               This removes what your agent currently knows about your growth edges, open commitments, and developmental patterns. Your conversation history and settings are not affected. The profile will rebuild naturally as you continue talking.
//             </Typography>
//           </DialogContent>
//           <DialogActions>
//             <Button onClick={() => this.setState({ confirmDelete: false })} size="small">
//               Cancel
//             </Button>
//             <Button onClick={() => this.deleteProfile()} color="secondary" size="small">
//               Delete
//             </Button>
//           </DialogActions>
//         </Dialog>
//       </>
//     );
//   }
// }

// export default withStyles(styles)(AgentDevProfilePanel);